import { analyticsEnabled, mixpanelKey, version } from '@/environment';
import { useFeatureFlag } from '@/services/featureFlags';
import { isUUIDv4 } from '@/utils/isUUID';
import { User } from '@auth0/auth0-react';
import type * as ld from 'launchdarkly-js-client-sdk';
import type { OverridedMixpanel } from 'mixpanel-browser';
import mixpanel from 'mixpanel-browser';
import React, { useEffect } from 'react';

export type { OverridedMixpanel } from 'mixpanel-browser';

export type ExtendedMixpanel = OverridedMixpanel & { enrich: (props: Record<string, any>) => void };

const Store = React.createContext<ExtendedMixpanel | null>({} as any);
Store.displayName = 'mixpanel';

const enabled = analyticsEnabled && window.location.hostname !== 'localhost';

export const useAnalytics = () => React.useContext(Store);

let superProps: Record<string, any> = {
    version,
};

const getExtendedMixpanel = (): ExtendedMixpanel => {
    const extendedMixpanel = mixpanel as ExtendedMixpanel;
    extendedMixpanel.enrich = newProps => {
        superProps = { ...superProps, ...newProps };
        extendedMixpanel.register(superProps);
    };

    extendedMixpanel.init(mixpanelKey, {
        track_pageview: 'url-with-path',
        record_sessions_percent: 10, //records 10% of all sessions
        // @ts-ignore // todo - its ts-ignore because types package is old, there is no new version yet, need to update it when will be published
        record_heatmap_data: true,
        record_min_ms: 8000, // minimum length of a single replay in milliseconds
        record_mask_text_selector: '', // it will prevent masking text
    });

    if (enabled) {
        extendedMixpanel.opt_in_tracking();
    } else {
        console.warn('Mixpanel disabled');
        extendedMixpanel.opt_out_tracking();
    }

    window.mixpanel = extendedMixpanel;

    return extendedMixpanel;
};
getExtendedMixpanel();

const localStorageKey = 'mixpanel_user_to_alias';

const createMixpanelAlias = (mixpanel: ExtendedMixpanel, userSub: string | undefined) => {
    if (!userSub) {
        return;
    }

    const uuidToAlias = localStorage.getItem(localStorageKey);

    if (!uuidToAlias) {
        return;
    }

    mixpanel.alias(uuidToAlias, userSub);
    localStorage.removeItem(localStorageKey);
};

const saveUUIDToAlias = (uuid: string | undefined) => {
    if (!uuid || !isUUIDv4(uuid)) return;

    localStorage.setItem(localStorageKey, uuid);
};

export const AnalyticsProvider = ({ children }: any) => {
    const instance = getMixpanel() || null;
    return <Store.Provider value={instance}>{children}</Store.Provider>;
};

export const useSingleAnalytic = (track: (mixpanel: ExtendedMixpanel) => void) => {
    const mixpanel = useAnalytics();
    useEffect(() => {
        mixpanel && track(mixpanel);
    }, []);
};

export const useIdentifyUser = () => {
    const mixpanel = useAnalytics();
    const featureFlagProvider = useFeatureFlag();
    let featureFlags: ld.LDFlagSet | null = null;

    const getFeatureFlags = async (): Promise<Record<string, string> | null> => {
        if (featureFlagProvider && featureFlagProvider.allFlags !== undefined) {
            const flagEntries = Object.entries(featureFlagProvider.allFlags());
            return flagEntries.reduce((acc, cur) => ({ ...acc, [`ff_${cur[0]}`]: cur[1] }), {});
        }
        return null;
    };

    return async (user: User) => {
        if (mixpanel) {
            saveUUIDToAlias(user.sub);

            if (!user.isAnonymous) {
                createMixpanelAlias(mixpanel, user.sub);
            }

            mixpanel.identify(user.sub);

            const { email, given_name, name, family_name, picture, ...props } = user;
            let retries = 0;
            const maxRetries = 5;
            const mixpanelUserName = given_name || email; // Auth0 stores email as name for non-SSO users
            let mixpanelProps: Record<string, any> = { ...props, given_name, $name: mixpanelUserName, $email: email, $avatar: picture };

            // try to retreive feature flags
            while (retries < maxRetries && !featureFlags) {
                featureFlags = await getFeatureFlags();
                if (!featureFlags) {
                    await new Promise(resolve => setTimeout(resolve, 200));
                    retries++;
                }
            }

            if (featureFlags) {
                mixpanelProps = { ...mixpanelProps, user_flags: featureFlags };
            }

            mixpanel.people.set(mixpanelProps);
        }
    };
};

export const getMixpanel = () => window.mixpanel;
